// src/hooks/useInViewPort.tsx
import { useState, useEffect, useRef } from "react";

function useInViewPort<T extends HTMLElement | undefined>(
  ref: React.RefObject<T>,
  options?: IntersectionObserverInit,
) {
  const [inViewport, setInViewport] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const prevRef = useRef<T | null>(null); // Store the previous ref

  useEffect(() => {
    // Create the IntersectionObserver
    const observer = new IntersectionObserver(([entry]) => {
      setInViewport(entry.isIntersecting);
    }, options);

    observerRef.current = observer;

    // Unobserve the previous ref if it exists and is different from the current ref
    if (
      prevRef.current &&
      prevRef.current !== ref.current &&
      prevRef.current instanceof HTMLElement
    ) {
      observer.unobserve(prevRef.current);
    }

    // Observe the current ref if it exists
    const currentRef = ref.current;
    if (currentRef instanceof HTMLElement) {
      observer.observe(currentRef);
    }

    // Update the previous ref to the current one
    prevRef.current = currentRef;

    // Cleanup on unmount or when ref/options change
    return () => {
      if (currentRef instanceof HTMLElement) {
        observer.unobserve(currentRef);
      }
      observer.disconnect(); // Disconnect the observer to avoid memory leaks
    };
  }, [options, ref]);

  return inViewport;
}

export default useInViewPort;
